// Modelo del formulario «Nuevo código»: esquema zod en espejo de CreateDiscountCodeRequest y
// conversión de lo que se teclea (pesos, %, días CDMX) al body.
import { z } from 'zod';
import { ApiError } from '@/api/errors';
import { mxDay, mxDayEndUtc, mxDayOffset, mxDayStartUtc } from '@/lib/dateHelpers';
import type {
    CreateDiscountCodeRequest,
    DiscountCode,
    DiscountType,
    PromotionPaymentOption,
    RetailPromotionsErrorCode,
} from '../api/promotionsContract';
import {
    discountIssue,
    discountText,
    discountValue,
    FORM_MESSAGES,
    isCount,
    type Issue,
} from './salePromotionForm';
import { PAYMENT_OPTION_VALUES } from './salePromotionFormOptions';
import {
    CODE_TEXT_RULE,
    DISCOUNT_CODE_FIELD_LABELS,
    PAST_VALID_UNTIL,
    serverFieldMessage,
} from './serverFieldErrors';

const CODE_MAX_LENGTH = 32;
// Espejo de CODE_TEXT_PATTERN del back, sobre el texto ya en mayúsculas.
const CODE_TEXT_PATTERN = /^[A-Z0-9Ñ_-]+$/u;
const CODE_TEXT_TAKEN: RetailPromotionsErrorCode = 'CODE_TEXT_TAKEN';
const INVALID_DISCOUNT_CODE: RetailPromotionsErrorCode = 'INVALID_DISCOUNT_CODE';

export const CODE_FORM_MESSAGES = {
    codeText: 'Escribe el código que el cliente va a dar en caja.',
    codeSpaces: 'El código no lleva espacios.',
    codeLength: `Usa hasta ${String(CODE_MAX_LENGTH)} caracteres.`,
    codeChars: CODE_TEXT_RULE,
    pastValidUntil: PAST_VALID_UNTIL,
    codeTaken: 'Ya hay un código vigente con ese texto. Elige otro.',
    maxUses: FORM_MESSAGES.count,
    range: FORM_MESSAGES.range,
    invalid:
        'Hay un dato del código que no es válido. Revisa el descuento, las fechas y los usos y guarda de nuevo.',
    saveErrorTitle: 'No se pudo crear el código',
    unknown: 'No pudimos crear el código. Intenta de nuevo en un momento.',
} as const;

export interface DiscountCodeFormValues {
    codeText: string;
    discountType: DiscountType;
    discountValue: string;
    /** Día CDMX `YYYY-MM-DD`; vacío = sin inicio. */
    validFrom: string;
    /** Vacío = sin fin. */
    validUntil: string;
    /** Vacío = sin tope. */
    maxUses: string;
    skus: string[];
    storeIds: string[];
    announceInStore: boolean;
    /** Vacía = cualquier forma de pago. */
    paymentOptions: PromotionPaymentOption[];
}

function codeTextIssue(text: string): string | null {
    const trimmed = text.trim();
    if (trimmed === '') return CODE_FORM_MESSAGES.codeText;
    if (/\s/.test(trimmed)) return CODE_FORM_MESSAGES.codeSpaces;
    if (trimmed.length > CODE_MAX_LENGTH) return CODE_FORM_MESSAGES.codeLength;
    if (!CODE_TEXT_PATTERN.test(trimmed.toUpperCase())) return CODE_FORM_MESSAGES.codeChars;
    return null;
}

function formIssues(v: DiscountCodeFormValues): Issue[] {
    const issues: (Issue | null)[] = [];
    const codeText = codeTextIssue(v.codeText);
    if (codeText) issues.push([['codeText'], codeText]);
    issues.push(discountIssue(v.discountType, v.discountValue, ['discountValue']));
    if (v.maxUses.trim() !== '' && !isCount(v.maxUses))
        issues.push([['maxUses'], CODE_FORM_MESSAGES.maxUses]);
    if (v.validFrom !== '' && v.validUntil !== '' && v.validUntil < v.validFrom)
        issues.push([['validUntil'], CODE_FORM_MESSAGES.range]);
    else if (v.validUntil !== '' && v.validUntil < mxDayOffset(0))
        issues.push([['validUntil'], CODE_FORM_MESSAGES.pastValidUntil]);
    return issues.filter((issue): issue is Issue => issue !== null);
}

export const discountCodeFormSchema = z
    .object({
        codeText: z.string(),
        discountType: z.enum(['AMOUNT', 'PERCENTAGE']),
        discountValue: z.string(),
        validFrom: z.string(),
        validUntil: z.string(),
        maxUses: z.string(),
        skus: z.array(z.string()),
        storeIds: z.array(z.string()),
        announceInStore: z.boolean(),
        paymentOptions: z.array(z.enum(PAYMENT_OPTION_VALUES)),
    })
    .superRefine((values, ctx) => {
        for (const [path, message] of formIssues(values)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
        }
    });

export function emptyDiscountCodeForm(): DiscountCodeFormValues {
    return {
        codeText: '',
        discountType: 'AMOUNT',
        discountValue: '',
        validFrom: '',
        validUntil: '',
        maxUses: '',
        skus: [],
        storeIds: [],
        announceInStore: false,
        paymentOptions: ['SUREKEEP'],
    };
}

/** Un código ya creado en el formulario, para verlo de solo lectura con el mismo editor del alta. */
export function discountCodeToFormValues(code: DiscountCode): DiscountCodeFormValues {
    return {
        codeText: code.codeText,
        discountType: code.discount.type,
        discountValue: discountText(code.discount),
        validFrom: code.validFrom === null ? '' : mxDay(code.validFrom),
        validUntil: code.validUntil === null ? '' : mxDay(code.validUntil),
        maxUses: code.maxUses === null ? '' : String(code.maxUses),
        skus: code.skus ?? [],
        storeIds: code.storeIds ?? [],
        announceInStore: code.announceInStore,
        paymentOptions: code.paymentOptions ?? [],
    };
}

const listOrNull = <T>(list: T[]): T[] | null => (list.length > 0 ? list : null);

export function toCreateDiscountCodeRequest(v: DiscountCodeFormValues): CreateDiscountCodeRequest {
    return {
        codeText: v.codeText.trim().toUpperCase(),
        discount: discountValue(v.discountType, v.discountValue),
        validFrom: v.validFrom === '' ? null : mxDayStartUtc(v.validFrom),
        validUntil: v.validUntil === '' ? null : mxDayEndUtc(v.validUntil),
        maxUses: v.maxUses.trim() === '' ? null : Number(v.maxUses),
        skus: listOrNull(v.skus),
        storeIds: listOrNull(v.storeIds),
        announceInStore: v.announceInStore,
        paymentOptions: listOrNull(v.paymentOptions),
    };
}

export interface DiscountCodeSaveError {
    /** `codeText` si el error es del texto; `null` si va en un toast. */
    field: 'codeText' | null;
    message: string;
}

export function discountCodeSaveError(error: unknown): DiscountCodeSaveError {
    if (error instanceof ApiError && error.code === CODE_TEXT_TAKEN)
        return { field: 'codeText', message: CODE_FORM_MESSAGES.codeTaken };
    if (error instanceof ApiError && error.code === INVALID_DISCOUNT_CODE) {
        const message = serverFieldMessage(error, DISCOUNT_CODE_FIELD_LABELS);
        const isCodeText = (error.data as { field?: unknown } | undefined)?.field === 'codeText';
        return {
            field: isCodeText ? 'codeText' : null,
            message: message ?? CODE_FORM_MESSAGES.invalid,
        };
    }
    if (error instanceof Error && error.message) return { field: null, message: error.message };
    return { field: null, message: CODE_FORM_MESSAGES.unknown };
}
